import type { IMutableContext } from '@unleash/proxy-client-react'

/**
 * Leading hostname segment — the tenant/organization identifier in this
 * platform (e.g. `trendsetter`, `skalx`). Sent to Unleash as the `subdomain`
 * context property so strategy constraints can do multi-tenant targeting
 * server-side (e.g. `subdomain IN [trendsetter]`).
 *
 * Returns '' for localhost / IPs / SSR so those never match a tenant
 * constraint.
 */
export const getSubdomain = (hostname: string): string => {
  const host = hostname?.toLowerCase() ?? ''
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return ''
  }

  return host.split('.')[0] ?? ''
}

/**
 * Build the Unleash evaluation context for the current session. The custom
 * `subdomain` property is what multi-tenant strategy constraints key off; it
 * is also mirrored to `tenant` so constraints can be written against either
 * name. account-ui is user-scoped rather than tenant-scoped, so it does not yet
 * use subdomain targeting — but the context is sent anyway to stay identical to
 * the ebs platform's `@shared/feature-flags`, so a tenant flag can be added
 * later with no client change.
 */
export const getUnleashContext = (): IMutableContext => {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname
  const subdomain = getSubdomain(hostname)

  return {
    properties: {
      subdomain,
      tenant: subdomain
    }
  }
}
